"use client";

import { PermissionGate } from "@/modules/auth/presentation/ui/PermissionGate";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import {
    BarChart3Icon,
    FileTextIcon,
    PackageIcon,
    PlusIcon,
    ShoppingBagIcon,
    WalletIcon,
} from "lucide-react";

export type DashboardQuickActionsProps = {
  onCreateOrder?: () => void;
  canCreateOrder?: boolean;
};

export function DashboardQuickActions({
  onCreateOrder,
  canCreateOrder = false,
}: DashboardQuickActionsProps) {
  const actions = [
    {
      title: "ອໍເດີ",
      icon: ShoppingBagIcon,
      url: "/app/orders",
      color: "bg-blue-500/10 text-blue-600",
      permissions: [Permissions.orders.read],
    },
    {
      title: "ບັນຊີ Wallet",
      icon: WalletIcon,
      url: "/app/wallet/accounts",
      color: "bg-green-500/10 text-green-600",
      permissions: [Permissions.wallet.read],
    },
    {
      title: "ສິນຄ້າ",
      icon: PackageIcon,
      url: "/app/catalog/products",
      color: "bg-purple-500/10 text-purple-600",
      permissions: [Permissions.catalog.read],
    },
    {
      title: "ລາຍງານ",
      icon: BarChart3Icon,
      url: "/app/reports",
      color: "bg-orange-500/10 text-orange-600",
      permissions: [
        Permissions.reports.sales.read,
        Permissions.reports.ops.read,
        Permissions.reports.wallet.read,
        Permissions.reports.inventory.read,
      ],
    },
    {
      title: "ສາງສິນຄ້າ",
      icon: FileTextIcon,
      url: "/app/reports/inventory",
      color: "bg-red-500/10 text-red-600",
      permissions: [
        Permissions.reports.inventory.read,
        Permissions.inventory.read,
      ],
    },
  ];

  return (
    <Card className="rounded-xl border bg-card">
      <CardContent className="">
        <h3 className="mb-4 font-semibold text-lg">ການດຳເນີນການດ່ວນ</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {canCreateOrder && (
            <button
              type="button"
              onClick={onCreateOrder}
              className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-4 transition-colors hover:bg-muted"
            >
              <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                <PlusIcon className="size-5 text-primary" />
              </div>
              <span className="font-medium text-sm">ສ້າງອໍເດີ</span>
            </button>
          )}
          {actions.map((action) => (
            <PermissionGate key={action.url} any={action.permissions}>
              <Link
                to={action.url}
                className="flex flex-col items-center gap-2 rounded-lg border p-4 transition-colors hover:bg-muted"
              >
                <div
                  className={`flex size-10 items-center justify-center rounded-lg ${action.color}`}
                >
                  <action.icon className="size-5" />
                </div>
                <span className="font-medium text-sm">{action.title}</span>
              </Link>
            </PermissionGate>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
